import { Link } from '@tanstack/react-router';
import { postMeta } from './manifest';
import type { Post } from './types';
import { H2, OL, P, Pull, UL } from './ui';

const post: Post = { meta: postMeta('beat-battle-theme-ideas'), Body };
export default post;

function Body() {
  return (
    <>
      <P>
        Running out of fresh prompts for your beat battle nights? Whether you host a Discord cypher,
        a campus battle, or you just need a constraint that pulls you out of a producer rut, this is
        a long working list. Every theme below has been tested in actual battles - on community
        Discord servers, in private rooms on Prod Battle, and at in-person producer meetups - so the
        ones that fall apart in practice are not on here.
      </P>

      <Pull>
        Good themes do two things: they level the playing field (everyone starts from the same
        creative jumping-off point) and they force originality (so you cannot just submit your last
        loop with a new name).
      </Pull>

      <P>
        We have grouped 50+ ideas into five categories - sample-flip prompts, constraint themes,
        genre-fusion themes, mood/visual themes, and gimmick rounds. Pick one, set a timer, ship a
        beat. Or steal the whole list and run a season.
      </P>

      <H2 id="how-to-pick-a-theme">How to pick a theme that actually works</H2>

      <P>
        Three things separate a battle theme that produces real submissions from one that fizzles.
      </P>

      <UL>
        <li>
          <strong>Specific enough to constrain, loose enough to vary.</strong> "Trap beat" is too
          loose - everyone makes their default. "Trap beat using only an 80s soap-opera vocal sample
          and no 808 sub" is a theme that produces different submissions from different producers.
        </li>
        <li>
          <strong>Plays well in 60-90 seconds.</strong> If the theme needs a 3-minute arrangement to
          land, it will lose votes against a punchier entry. Themes that pop in the first 8 bars are
          themes that win.
        </li>
        <li>
          <strong>Has a clear "did you do the assignment?" check.</strong> Voters should be able to
          tell at a glance whether each entry actually followed the prompt. Vague themes get gamed.
        </li>
      </UL>

      <H2 id="sample-flip-themes">Sample-flip themes (15 ideas)</H2>

      <P>
        The classic format. One sample, twenty interpretations. Pair it with our{' '}
        <Link to="/play" className="underline">
          Sample Flip mode
        </Link>{' '}
        if you want this done automatically with audio fingerprinting and blind voting.
      </P>

      <OL>
        <li>
          <strong>Forgotten era flip.</strong> Pick a sample from a 1970s soul deep cut, a 1980s
          synth-pop B-side, or a 1990s anime OST. The more obscure the source, the more the
          producer's chops show.
        </li>
        <li>
          <strong>Vocal-only flip.</strong> One isolated vocal phrase (3-6 seconds). Producers must
          build the entire beat around it.
        </li>
        <li>
          <strong>One-shot flip.</strong> One single drum hit, one single melodic note. Build a full
          track from those two sounds (plus whatever you can synthesize from them).
        </li>
        <li>
          <strong>Movie dialogue flip.</strong> Use a famous movie quote as the only sample. Bonus
          rule: the line has to remain intelligible somewhere in the track.
        </li>
        <li>
          <strong>Same sample, two genres.</strong> Producers must submit two entries from the same
          source sample, in two different genres. Tests range.
        </li>
        <li>
          <strong>Game audio flip.</strong> Sample from a video game soundtrack (Final Fantasy VII,
          Earthbound, Pokemon Gold/Silver). Crowd loves the nostalgia.
        </li>
        <li>
          <strong>News clip flip.</strong> A 10-second news anchor clip. Forces creative chopping
          and pitch work.
        </li>
        <li>
          <strong>Field recording flip.</strong> Host records ambient audio (subway, rain, market)
          and shares it as the only allowed sample.
        </li>
        <li>
          <strong>Royalty-free chaos.</strong> Use only samples from the same Splice or Loopcloud
          pack. Producers prove who can squeeze the most out of identical material.
        </li>
        <li>
          <strong>Acapella flip.</strong> Full acapella from a public-domain track. Producers choose
          how much to leave in.
        </li>
        <li>
          <strong>Single instrument flip.</strong> One acoustic guitar loop, one piano loop, one
          saxophone phrase. Choose one.
        </li>
        <li>
          <strong>Cartoon theme flip.</strong> Saturday morning cartoons (Looney Tunes, Tom &amp;
          Jerry). The melodic material is already meme-worthy.
        </li>
        <li>
          <strong>Foreign-language flip.</strong> Sample a track in a language none of the producers
          speak natively. Removes lyrical bias.
        </li>
        <li>
          <strong>Live instrument flip.</strong> Host plays 30 seconds on guitar/piano/synth at the
          start of the battle. That recording is the only allowed sample.
        </li>
        <li>
          <strong>Public-domain classical flip.</strong> Beethoven, Bach, Debussy. Sample a phrase
          that has been flipped a thousand times and force a fresh take anyway.
        </li>
      </OL>

      <H2 id="constraint-themes">Constraint themes (12 ideas)</H2>

      <P>Constraint themes are the producer equivalent of a poetry form. The limit is the point.</P>

      <OL>
        <li>
          <strong>No 808.</strong> Make a beat that hits without the genre-default sub. Forces
          arrangement and mix work.
        </li>
        <li>
          <strong>One synth, one drum kit.</strong> Pick a single VST/sample and a single drum kit.
          No stacking. No outside layers.
        </li>
        <li>
          <strong>120 BPM only.</strong> Every entry locked at the same tempo. Tests arrangement,
          not energy.
        </li>
        <li>
          <strong>Mono only.</strong> Final bounce in mono. Sees who actually knows how to arrange
          (panning hides a lot of lazy mixing).
        </li>
        <li>
          <strong>No copy-paste.</strong> Every bar must be different. Forces real arrangement.
        </li>
        <li>
          <strong>Single MIDI channel.</strong> One synth patch, played monophonically. Bass and
          melody come from the same note stream.
        </li>
        <li>
          <strong>10-minute timer.</strong> From open-DAW to bounced beat in 10 minutes. Live stream
          the whole thing for vote weight.
        </li>
        <li>
          <strong>No drums until the drop.</strong> The first 16 bars must be drumless. Tests
          intro-building.
        </li>
        <li>
          <strong>Stems-only mix.</strong> No final master, no limiter, no bus compression. Voters
          judge raw production.
        </li>
        <li>
          <strong>50 BPM apart.</strong> First half-tempo, then double-time, in the same track.
        </li>
        <li>
          <strong>Drum machine only.</strong> All percussion from a single hardware drum machine (or
          one VST emulation). Forces character.
        </li>
        <li>
          <strong>No reverb, no delay.</strong> Dry mix only. Brutal but enlightening.
        </li>
      </OL>

      <H2 id="genre-fusion-themes">Genre-fusion themes (10 ideas)</H2>

      <P>
        Two genres, one beat. These themes punish defaults and reward producers who can hear two
        traditions in their head at once.
      </P>

      <OL>
        <li>Trap drums, drum-and-bass tempo</li>
        <li>Lo-fi melodies, phonk bass</li>
        <li>Boom bap drums, house energy</li>
        <li>Amapiano log drums, hip-hop arrangement</li>
        <li>Jersey club bounce, ambient pads</li>
        <li>Hyperpop chops, jazz changes</li>
        <li>Detroit techno, southern trap drums</li>
        <li>UK garage swing, Latin percussion</li>
        <li>Funk bass, dubstep wobble</li>
        <li>Bossa nova chords, drill flow</li>
      </OL>

      <H2 id="mood-themes">Mood and visual themes (10 ideas)</H2>

      <P>
        Mood themes are the open-ended ones that let producers express. They are also the easiest to
        vote on - your gut tells you immediately whether the producer hit the vibe.
      </P>

      <UL>
        <li>3am driving alone</li>
        <li>Empty subway station</li>
        <li>Rooftop in summer</li>
        <li>Anime tournament arc</li>
        <li>Heist getaway</li>
        <li>Final boss fight</li>
        <li>Wedding after-party at 2am</li>
        <li>Walking into a cold gym</li>
        <li>Lost in a market in Bangkok</li>
        <li>Underwater dream sequence</li>
      </UL>

      <H2 id="gimmick-rounds">Gimmick rounds (8 ideas - use sparingly)</H2>

      <P>
        Save these for special events. They get loud reactions but burn out fast if you run them
        every week.
      </P>

      <UL>
        <li>
          <strong>Blind tempo.</strong> Producers do not know the BPM until the kit drops at the
          start of the round.
        </li>
        <li>
          <strong>Coin flip key.</strong> Major or minor decided live by a coin toss right before
          the timer starts.
        </li>
        <li>
          <strong>Reverse engineer.</strong> Host plays a finished beat once. Producers have to
          recreate it from memory.
        </li>
        <li>
          <strong>Three-way collab.</strong> Random pairs of producers each contribute one 8-bar
          section. The host stitches them.
        </li>
        <li>
          <strong>Theme jar.</strong> Producers draw three random words from a hat. The beat has to
          evoke all three.
        </li>
        <li>
          <strong>Last hit standing.</strong> 60-second beats, knockout bracket, single elimination,
          no time between rounds.
        </li>
        <li>
          <strong>Sample bidding.</strong> Producers bid in-game currency (or fake currency) for the
          right to claim specific sounds from a shared kit.
        </li>
        <li>
          <strong>Audience prompt.</strong> Theme is generated live by a Twitch chat vote or Discord
          poll in the minute before the round starts.
        </li>
      </UL>

      <H2 id="how-to-run-a-themed-season">How to run a themed season</H2>

      <P>
        If you host weekly, do not pick themes at random. Plan a season - 6 to 8 weeks of related
        prompts that build on each other. Producers stick around when there is a narrative.
      </P>

      <P>An example 8-week season we have seen work:</P>

      <OL>
        <li>Week 1 - Era flip (1980s)</li>
        <li>Week 2 - Era flip (1990s)</li>
        <li>Week 3 - Constraint: no 808</li>
        <li>Week 4 - Genre fusion: drill + lo-fi</li>
        <li>Week 5 - Mood: rooftop in summer</li>
        <li>Week 6 - Sample flip: foreign-language vocal</li>
        <li>Week 7 - Gimmick: blind tempo</li>
        <li>Week 8 - Grand finale: producer's pick (returning champions only)</li>
      </OL>

      <H2 id="run-it-on-prod-battle">Run any of these on Prod Battle</H2>

      <P>
        If you want a private room with the timing, kit, blind voting, and ranking handled for you,
        you can{' '}
        <Link to="/play" className="underline">
          start a private battle
        </Link>{' '}
        in under a minute. Pick any theme above, share the room code, and your community shows up.
        For sample-flip themes specifically, the{' '}
        <Link to="/beat-battles/$genre" params={{ genre: 'trap' }} className="underline">
          Sample Flip mode
        </Link>{' '}
        handles the kit distribution and the per-track audio fingerprinting that catches the
        producer who shows up with a pre-made beat. The{' '}
        <Link to="/leaderboard" className="underline">
          leaderboard
        </Link>{' '}
        carries season wins forward, so themed series build long-term standings.
      </P>

      <P>
        Got a theme that has worked in your community and is not on this list? Mention it on our
        Discord (linked from the homepage) and we will add the best ones.
      </P>
    </>
  );
}
